import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Calendar, Phone, Mail, Clock, User, Tag, MoreHorizontal, Clock as ClockIcon, FileEdit, Paperclip, Link2, ShoppingBag, Trash2, UserPlus } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getUserName } from "@/services/userService";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useHideDeactivatedUsers } from "@/hooks/use-hide-deactivated-users";
import { deleteLead, updateLead } from "@/services/leadsService";

// Define the lead status type
export type LeadStatus =
  | "new"
  | "old-contact"
  | "not-reached"
  | "follow-up"
  | "callback"
  | "scheduled"
  | "in-progress"
  | "won"
  | "lost"
  | "offer"
  | "negotiation"
  | "paused"
  | "wrong-data"
  | "delete-data";

export type LeadPriority = "low" | "medium" | "high";

// Define history change type
export type HistoryChange = {
  field: string;
  oldValue: any;
  newValue: any;
};

// Define history entry type
export type HistoryEntry = {
  date: string;
  type: "created" | "updated" | "deleted" | "note_added" | "note_updated" | "note_deleted" | "marketplace_assignment";
  changes: HistoryChange[];
  user: string;
  noteContent?: string; // For note-specific actions
};

// Define notes entry type
export type NotesEntry = {
  id: string;
  content: string;
  timestamp: string;
  createdBy: string;
};

export interface Lead {
  id: string;
  firstName: string;
  lastName: string;
  position: string;
  phone: string;
  status: LeadStatus;
  priority: LeadPriority;
  nextContactDate?: Date | string;
  lastContactDate?: Date | string;
  notes: string;
  notesHistory?: NotesEntry[];
  assignedTo: string;
  attachment?: string;
  history?: HistoryEntry[];
  marketplacePurchaseInfo?: string;
  dataProtectionConsent?: boolean;
  createdAt?: Date | string;
  email?: string;
  lastUpdated?: Date | string;
  source?: string;
  marketLeadId?: string;
  tenantId?: string;
  company?: string;
  profession?: string;
  folderId?: string;
}

// Status labels in German
export const STATUS_LABELS: Record<LeadStatus, string> = {
  "new": "Neu",
  "old-contact": "Altkontakt",
  "not-reached": "Nicht erreicht",
  "follow-up": "Wiedervorlage",
  "callback": "Rückruf",
  "scheduled": "Terminiert",
  "in-progress": "In Prozess",
  "won": "Gewonnen",
  "lost": "Verloren",
  "offer": "Angebot",
  "negotiation": "Verhandlung",
  "paused": "Pausiert",
  "wrong-data": "Falsche Daten",
  "delete-data": "Daten löschen"
};

// Status colors
export const STATUS_COLORS: Record<LeadStatus, string> = {
  "new": "bg-purple-500",
  "old-contact": "bg-gray-500",
  "not-reached": "bg-blue-500",
  "follow-up": "bg-amber-400",
  "callback": "bg-amber-500",
  "scheduled": "bg-indigo-500",
  "in-progress": "bg-orange-500",
  "won": "bg-green-500",
  "lost": "bg-red-500",
  "offer": "bg-purple-400",
  "negotiation": "bg-blue-400",
  "paused": "bg-gray-400",
  "wrong-data": "bg-yellow-500",
  "delete-data": "bg-red-700"
};

const PRIORITY_LABELS: Record<LeadPriority, string> = {
  "low": "Niedrig",
  "medium": "Mittel",
  "high": "Hoch"
};

const PRIORITY_COLORS: Record<LeadPriority, { bg: string, text: string }> = {
  "low": { bg: "bg-blue-50", text: "text-blue-600" },
  "medium": { bg: "bg-amber-50", text: "text-amber-600" },
  "high": { bg: "bg-red-50", text: "text-red-600" }
};

// Helper function to safely format dates
const safeFormatDate = (date: Date | string | undefined, formatStr: string): string => {
  if (!date) return "";

  try {
    // Ensure we have a valid Date object
    const dateObj = date instanceof Date ? date : new Date(date);

    // Check if date is valid before formatting
    if (isNaN(dateObj.getTime())) {
      return "";
    }

    return format(dateObj, formatStr, { locale: de });
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

// Helper function to get the last history entry date
const getLastUpdateDate = (history?: HistoryEntry[]): string => {
  if (!history || history.length === 0) return "";

  // Sort history by date (newest first)
  const sortedHistory = [...history].sort((a, b) =>
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return safeFormatDate(sortedHistory[0].date, "dd.MM.yyyy");
};

// Helper function to count total changes in history
const countHistoryChanges = (history?: HistoryEntry[]): number => {
  if (!history) return 0;

  return history.reduce((total, entry) => {
    return total + (entry.changes?.length || 0);
  }, 0);
};

// Helper function to check if lead was automatically listed by system
const isSystemListedLead = (lead: Lead): boolean => {
  return lead.marketplacePurchaseInfo?.includes("Über Marktplatz zugewiesen von System") || false;
};

interface LeadCardProps {
  lead: Lead;
  onClick?: (lead: Lead) => void;
  view?: "kanban" | "list" | "table";
  onLeadDelete?: (leadId: string) => void;
  onLeadAssign?: (leadId: string, userId: string) => void;
  onLeadTakeover?: (leadId: string) => void;
  users?: Array<{ id: string; name: string; email: string; role: string; active?: boolean; }>;
}

export function LeadCard({
  lead,
  onClick,
  view = "kanban",
  onLeadDelete,
  onLeadAssign,
  onLeadTakeover,
  users = []
}: LeadCardProps) {
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const [hideDeactivated] = useHideDeactivatedUsers();
  const visibleUsers = hideDeactivated ? users.filter(u => u.active !== false) : users;
  const isCallback = lead.status === "callback" && lead.nextContactDate;
  const isOverdue = isCallback && lead.nextContactDate && new Date() > new Date(lead.nextContactDate);
  const isPurchasedLead = lead.marketplacePurchaseInfo || (lead.notes && lead.notes.includes("Gekauft von"));
  const isSystemListed = isSystemListedLead(lead);
  const [assignedUserName, setAssignedUserName] = useState<string>("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);

  useEffect(() => {
    const loadUserName = async () => {
      if (lead.assignedTo) {
        // First try to find the user in the users prop
        const user = users.find(u => u.id === lead.assignedTo);
        if (user) {
          setAssignedUserName(user.name);
        } else {
          // Fallback to fetching the name from the service
          const name = await getUserName(lead.assignedTo);
          setAssignedUserName(name);
        }
      }
    };

    loadUserName();
  }, [lead.assignedTo, users]);

  const handleClick = () => {
    if (onClick) onClick(lead);
  };

  const handleDetailsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClick) onClick(lead);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (onLeadDelete) {
      onLeadDelete(lead.id);
    }
    setIsDeleteDialogOpen(false);
  };

  const handleTakeoverClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onLeadTakeover && currentUser) {
      onLeadTakeover(lead.id);
      toast({
        title: "Lead übernommen",
        description: "Der Lead wurde Ihnen zugewiesen",
      });
    }
  };

  const handleAssignClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (users.length === 0) {
      toast({
        title: "Fehler",
        description: "Keine Benutzer verfügbar",
        variant: "destructive",
      });
      return;
    }
    setIsAssignDialogOpen(true);
  };

  const handleAssignToUser = async (userId: string) => {
    if (onLeadAssign) {
      onLeadAssign(lead.id, userId);
      setIsAssignDialogOpen(false);
    }
  };

  if (view === "table") {
    return (
      <tr
        className={cn(
          "border-b hover:bg-gray-50 cursor-pointer transition-colors",
          isSystemListed && "bg-gray-200"
        )}
        onClick={handleClick}
      >
        <td className="px-4 py-2">
          <div className="flex items-center">
            <div className={cn("w-2 h-2 rounded-full mr-2", STATUS_COLORS[lead.status])}></div>
            <span className="text-sm text-gray-600">{STATUS_LABELS[lead.status]}</span>
          </div>
        </td>
        <td className="px-4 py-2">
          <div className="flex items-center">
            <span className="font-medium">{lead.firstName}</span>
          </div>
        </td>
        <td className="px-4 py-2 font-medium">{lead.lastName}</td>
        <td className="px-4 py-2">{lead.position || "-"}</td>
        <td className="px-4 py-2">{lead.phone || "-"}</td>
        <td className="px-4 py-2">{lead.attachment || "-"}</td>
        <td className="px-4 py-2">
          <Badge
            variant="outline"
            className={cn(
              "font-normal",
              PRIORITY_COLORS[lead.priority].bg,
              PRIORITY_COLORS[lead.priority].text
            )}
          >
            {PRIORITY_LABELS[lead.priority]}
          </Badge>
        </td>
        <td className="px-4 py-2">
          {safeFormatDate(lead.nextContactDate, "dd.MM.yyyy") || "-"}
        </td>
        <td className="px-4 py-2">
          <div className="flex items-center">
            <User className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
            <span className="text-gray-600 text-sm">{assignedUserName || "..."}</span>
          </div>
        </td>
        <td className="px-4 py-2 flex justify-end">
          <Button variant="ghost" size="sm" onClick={handleDetailsClick} className="text-gray-500">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </td>
      </tr>
    );
  }

  if (view === "list") {
    return (
      <div
        className={cn(
          "border border-gray-200 rounded-md shadow-sm hover:shadow-md transition-shadow cursor-pointer p-4",
          isSystemListed ? "bg-gray-200" : "bg-white"
        )}
        onClick={handleClick}
      >
        <div className="flex justify-between">
          <div className="flex-1">
            <div className="flex items-center mb-2">
              <div className={cn("w-2 h-2 rounded-full mr-2", STATUS_COLORS[lead.status])}></div>
              <span className="font-medium text-gray-900">{STATUS_LABELS[lead.status]}</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">{lead.firstName} {lead.lastName}</h3>
            <p className="text-sm text-gray-600 mb-3">{lead.position}</p>

            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1.5" />
                <span>{safeFormatDate(lead.nextContactDate, "dd.MM.yyyy") || "Kein Datum"}</span>
              </div>
              <div className="flex items-center">
                <FileEdit className="h-4 w-4 mr-1.5" />
                <span>{countHistoryChanges(lead.history)} Änderungen</span>
              </div>
              <div className="flex items-center">
                <ClockIcon className="h-4 w-4 mr-1.5" />
                <span>Letzte Änderung: {getLastUpdateDate(lead.history) || "Nie"}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end space-y-2">
            <Badge
              variant="outline"
              className={cn(
                "font-normal",
                PRIORITY_COLORS[lead.priority].bg,
                PRIORITY_COLORS[lead.priority].text
              )}
            >
              {PRIORITY_LABELS[lead.priority]}
            </Badge>

            <span className="text-xs text-gray-500">Zugewiesen</span>
            <div className="flex items-center">
              <User className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
              <span className="text-sm">{assignedUserName || "..."}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Kanban view
  return (
    <div
      className={cn(
        "border border-gray-200 rounded-md shadow-sm hover:shadow-md transition-shadow cursor-pointer",
        isSystemListed ? "bg-gray-200" : "bg-white"
      )}
      onClick={handleClick}
    >
      <div className="p-3 flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center flex-1">
            <div className={cn("w-2 h-2 rounded-full mr-2", STATUS_COLORS[lead.status])}></div>
            <span className="text-xs font-medium text-gray-500">{STATUS_LABELS[lead.status]}</span>
          </div>
          {currentUser?.role === "admin" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-gray-400"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Aktionen</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleTakeoverClick}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Lead übernehmen
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleAssignClick}>
                  <User className="h-4 w-4 mr-2" />
                  Zuweisen an...
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleDeleteClick}
                  className="text-red-600 focus:text-red-600"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Löschen
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <h3 className="font-semibold text-gray-900 mb-1">{lead.firstName} {lead.lastName}</h3>
        <p className="text-sm text-gray-600 mb-3">{lead.position}</p>

        <div className="flex items-center justify-between mt-auto">
          <div className="flex items-center">
            <User className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
            <span className="text-xs text-gray-500">{assignedUserName || "..."}</span>
          </div>

          <Badge
            variant="outline"
            className={cn(
              "font-normal text-xs",
              PRIORITY_COLORS[lead.priority].bg,
              PRIORITY_COLORS[lead.priority].text
            )}
          >
            {PRIORITY_LABELS[lead.priority]}
          </Badge>
        </div>

        <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
          <div className="flex items-center">
            <Calendar className="h-3.5 w-3.5 mr-1" />
            <span>{safeFormatDate(lead.nextContactDate, "dd.MM.yyyy") || "Kein Datum"}</span>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center">
              <FileEdit className="h-3.5 w-3.5 mr-1" />
              <span>{countHistoryChanges(lead.history)}</span>
            </div>
            <div className="flex items-center">
              <ClockIcon className="h-3.5 w-3.5 mr-1" />
              <span>{getLastUpdateDate(lead.history) || "Neu"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Lead löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie diesen Lead wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteConfirm();
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Assign to User Dialog */}
      <AlertDialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Lead zuweisen</AlertDialogTitle>
            <AlertDialogDescription>
              Wählen Sie einen Benutzer aus, dem der Lead zugewiesen werden soll.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <div className="max-h-[200px] overflow-y-auto space-y-2">
              {visibleUsers.length > 0 ? (
                visibleUsers.map((user) => (
                  <Button
                    key={user.id}
                    variant="ghost"
                    className={`w-full justify-start ${!user.active ? "text-muted-foreground opacity-60" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAssignToUser(user.id);
                    }}
                  >
                    <User className="h-4 w-4 mr-2" />
                    {user.name || "Unbekannter Benutzer"}
                  </Button>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-2">
                  Keine Benutzer verfügbar
                </p>
              )}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Abbrechen</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
